import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import cx from "classnames";
import { readFragment, ResultOf } from "gql.tada";
import { ReactNode } from "react";
import {
  SolutionsChapterFragment,
  SolutionsRowFragment,
} from "../../../queries/entries/SolutionsSectionById";
import { RichText } from "../../shared/core/RichText";
import { MediaItem } from "../../shared/MediaItem";
import SolutionsChapterRows from "./SolutionsChapterRows";
import cn from "classnames";
import { MarkdownLite } from "../../shared/MarkdownLite";

const SolutionsChapterHeading = ({ children }: { children: ReactNode }) =>
  children && <h4 className="text-heading-4 mb-[22px]">{children}</h4>;

const mediaSourceOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p className="text-label font-normal text-brand-cream">{children}</p>
    ),
  },
} satisfies Options;

const SolutionsChapter = ({
  chapter,
  index,
}: {
  chapter: ResultOf<typeof SolutionsChapterFragment>;
  index: number;
}) => {
  const rows = (chapter.rowsCollection?.items ?? [])
    .map((item) => readFragment(SolutionsRowFragment, item))
    .filter(Boolean);

  return (
    <div
      className={cn("flex flex-col dsk:mb-[100px] mb-[50px]", {
        "mt-20": index !== 0,
      })}
    >
      <div className="flex flex-col gap-10">
        {chapter.eyebrow && (
          <p className="text-body-large text-brand-cream">{chapter.eyebrow}</p>
        )}
        {chapter.title && (
          <h2 className="text-heading-1 w-full">
            <MarkdownLite>{chapter.title}</MarkdownLite>
          </h2>
        )}
      </div>
      {(chapter.media || chapter.mediaHeading || chapter.mediaBodyRichText) && (
        <div className="mt-20">
          {chapter.mediaLayout?.includes("Vertical") ? (
            <div
              className={cx(
                "mb-10 dsk:mt-10 flex flex-col gap-[43px]",
                chapter.mediaLayout === "Vertical (media on bottom)" &&
                  "flex-col-reverse"
              )}
            >
              <div className="flex flex-col items-start gap-2">
                <MediaItem
                  item={chapter.media}
                  className="w-full rounded-xl"
                  width={1000}
                />
                <RichText
                  content={chapter.mediaSourceRichText}
                  options={mediaSourceOptions}
                />
              </div>

              {(chapter.mediaHeading || chapter.mediaBodyRichText) && (
                <div className="w-full flex flex-col">
                  <SolutionsChapterHeading>
                    <MarkdownLite>{chapter.mediaHeading ?? ""}</MarkdownLite>
                  </SolutionsChapterHeading>
                  <div className="text-brand-cream">
                    <RichText content={chapter.mediaBodyRichText} />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className={cx(
                chapter.mediaLayout,
                "mb-10 dsk:mt-10 flex flex-col gap-[43px]",
                "dsk:grid dsk:grid-cols-[repeat(17,_minmax(0,_1fr))] dsk:gap-x-4 dsk:grid-flow-row-dense"
              )}
            >
              <div
                className={cx(
                  "flex flex-col dsk:col-span-6 dsk:justify-center",
                  {
                    "dsk:col-start-12":
                      chapter.mediaLayout === "Horizontal (media on left)",
                  }
                )}
              >
                <SolutionsChapterHeading>
                  <MarkdownLite>{chapter.mediaHeading ?? ""}</MarkdownLite>
                </SolutionsChapterHeading>
                <div className="text-brand-cream">
                  <RichText content={chapter.mediaBodyRichText} />
                </div>
              </div>
              <div
                className={cx(
                  "flex flex-col items-start gap-2 dsk:col-span-9",

                  chapter.mediaLayout === "Horizontal (media on left)"
                    ? "dsk:col-start-1"
                    : "dsk:col-start-9"
                )}
              >
                <MediaItem
                  item={chapter.media}
                  className="w-full rounded-xl"
                  width={1000}
                />
                <RichText
                  content={chapter.mediaSourceRichText}
                  options={mediaSourceOptions}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <SolutionsChapterRows
        rows={rows!}
        hasMedia={Boolean(
          chapter.mediaHeading ||
            chapter.media ||
            chapter.mediaBodyRichText ||
            chapter.mediaSourceRichText
        )}
      />
    </div>
  );
};

export default SolutionsChapter;
